import { useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { useAppUpdates } from "./useAppUpdates";

export type AppVersionPanelColors = {
  text: string;
  muted: string;
  border: string;
  /** Button fill. */
  accent: string;
  /** Text on the accent fill. */
  onAccent: string;
  danger: string;
};

type Props = {
  colors: AppVersionPanelColors;
  fontFamily?: string;
};

type Status = { kind: "idle" } | { kind: "up-to-date" | "error" | "disabled" };

const STATUS_TEXT: Record<Exclude<Status["kind"], "idle">, string> = {
  "up-to-date": "You have the latest version.",
  error: "Could not check for updates. Try again when you are online.",
  disabled: "Updates are not available in this build.",
};

/**
 * Self-contained version readout and "Check for updates" button. Uses only React Native primitives plus
 * the colours passed in, so it drops into any settings screen.
 */
export function AppVersionPanel({ colors, fontFamily }: Props) {
  const { info, isUpdatePending, isBusy, checkAndDownload, applyUpdate } =
    useAppUpdates();
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const nativeLabel = info.nativeVersion
    ? `${info.nativeVersion}${info.nativeBuild ? ` (${info.nativeBuild})` : ""}`
    : "unknown";
  const updateLabel = !info.updatesEnabled
    ? __DEV__
      ? "Disabled in development"
      : "Not available"
    : info.isEmbeddedLaunch
      ? "Built-in (no update applied)"
      : (info.updateId?.slice(0, 8) ?? "unknown");

  const onCheck = async () => {
    setStatus({ kind: "idle" });
    const result = await checkAndDownload();
    if (result === "up-to-date" || result === "error" || result === "disabled")
      setStatus({ kind: result });
  };

  const rows: [string, string][] = [
    ["App version", nativeLabel],
    ["Update", updateLabel],
  ];
  if (info.updatesEnabled && !info.isEmbeddedLaunch && info.updateCreatedAt)
    rows.push(["Published", info.updateCreatedAt.toLocaleString()]);
  if (info.channel) rows.push(["Channel", info.channel]);
  if (info.runtimeVersion)
    rows.push(["Runtime", info.runtimeVersion.slice(0, 8)]);
  rows.push(["Platform", info.platform]);

  const font = fontFamily ? { fontFamily } : null;
  const showButton = info.updatesEnabled;

  return (
    <View style={styles.root}>
      <Text style={[styles.headline, font, { color: colors.text }]}>
        v{info.nativeVersion ?? "?"}
        {info.updatesEnabled && !info.isEmbeddedLaunch && info.updateId
          ? ` · update ${info.updateId.slice(0, 8)}`
          : ""}
        {__DEV__ ? " · dev" : ""}
      </Text>
      {rows.map(([label, value]) => (
        <View
          key={label}
          style={[styles.row, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.label, font, { color: colors.muted }]}>
            {label}
          </Text>
          <Text style={[styles.value, font, { color: colors.text }]}>
            {value}
          </Text>
        </View>
      ))}
      {showButton ? (
        <Pressable
          accessibilityRole="button"
          disabled={isBusy}
          onPress={isUpdatePending ? () => void applyUpdate() : onCheck}
          style={({ pressed }) => [
            styles.button,
            {
              backgroundColor: colors.accent,
              opacity: isBusy ? 0.6 : pressed ? 0.85 : 1,
            },
          ]}
        >
          {isBusy ? (
            <ActivityIndicator color={colors.onAccent} size="small" />
          ) : null}
          <Text style={[styles.buttonText, font, { color: colors.onAccent }]}>
            {isBusy
              ? "Checking…"
              : isUpdatePending
                ? "Restart to apply update"
                : "Check for updates"}
          </Text>
        </Pressable>
      ) : null}
      {isUpdatePending ? (
        <Text style={[styles.status, font, { color: colors.text }]}>
          An update has been downloaded and is ready to apply.
        </Text>
      ) : status.kind !== "idle" ? (
        <Text
          style={[
            styles.status,
            font,
            { color: status.kind === "error" ? colors.danger : colors.muted },
          ]}
        >
          {STATUS_TEXT[status.kind]}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { gap: 8 },
  headline: { fontSize: 18, fontWeight: "600", marginBottom: 4 },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  label: { fontSize: 14 },
  value: { fontSize: 14, flexShrink: 1, textAlign: "right" },
  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 44,
    borderRadius: 8,
    paddingHorizontal: 16,
    marginTop: 8,
  },
  buttonText: { fontSize: 15, fontWeight: "600" },
  status: { fontSize: 13, marginTop: 2 },
});
